import {
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes
} from '@aws-amplify/auth';
import React, { createContext, useEffect, useState } from "react";
import UserProfileService from "../services/UserProfileService";
import { getMyPermissions } from "../services/SecurityRolesService";

export const DataContext = createContext();

// Custom hook that fetches user data and access token from Cognito
const useUserData = () => {
  const [userData, setUserData] = useState(null);
  const [accessToken, setAccessToken] = useState("");
  const [accountId, setAccountId] = useState(() => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        return window.localStorage.getItem("account_id") || null;
      }
    } catch {
      // ignore when storage fails
    }
    return null;
  });
  // Grupos de Cognito (`cognito:groups`, claim del ID token) -- reemplaza al
  // atributo custom `custom:is_root` que se usaba antes (removido: cualquier
  // usuario que se auto-registraba terminaba con `custom:is_root=true`,
  // editable desde el cliente -- un problema de seguridad real, no solo de
  // estilo). La pertenencia a un grupo de Cognito solo puede asignarse
  // server-side (AdminAddUserToGroup), así que es la fuente de autorización
  // real para "root" (plataforma) y "admin" (tenant).
  const [cognitoGroups, setCognitoGroups] = useState([]);

  // Permisos efectivos del usuario (claves granulares, ej.
  // "semantic.workspace.view") y rol asignado, resueltos vía
  // GET /permissions/me en whisperbi-back-security. Son un complemento a
  // cognitoGroups (root/admin siguen siendo la fuente de autorización para
  // esas dos operaciones de alto nivel) -- este endpoint puede no estar
  // desplegado todavía, así que se resuelve de forma tolerante a fallos.
  const [permissions, setPermissions] = useState([]);
  const [myRole, setMyRole] = useState(null);

  // `true` una vez que el efecto de abajo terminó de resolver la sesión de
  // Cognito (haya o no `accountId`). Los consumidores que dependen de
  // `accountId` deben esperar a este flag antes de decidir que "no hay
  // cuenta" -- si no, muestran un error en el primer render, mientras la
  // sesión todavía se está hidratando, y el usuario tiene que hacer F5.
  const [authResolved, setAuthResolved] = useState(false);

  const [selectedWorkspace, setSelectedWorkspace] = useState(() => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        const saved = window.localStorage.getItem("selected_workspace");
        return saved ? JSON.parse(saved) : null;
      }
    } catch (error) {
      console.warn("⚠️ Error loading workspace from localStorage:", error);
      return null;
    }
    return null;
  });

  // Agente elegido por workspace: { [semantic_workspace_id]: config_id }.
  // Persiste entre navegaciones/recargas hasta que el usuario elija otro
  // (o hasta que el agente guardado deje de existir -> AgentSelector lo
  // auto-corrige). El chat lo consume vía `selectedAgentId`.
  const [agentByWorkspace, setAgentByWorkspace] = useState(() => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        const saved = window.localStorage.getItem("selected_agent_by_workspace");
        const parsed = saved ? JSON.parse(saved) : null;
        return parsed && typeof parsed === "object" ? parsed : {};
      }
    } catch {
      // ignore
    }
    return {};
  });

  useEffect(() => {
    let isMounted = true; // evita setState tras unmount

    const fetchData = async () => {
      try {
        // Sesión y token PRIMERO: el ID token ya trae `custom:account_id` y
        // `cognito:groups` en su payload (Amplify lo persiste en storage al
        // hacer login), así que es la fuente más confiable y disponible de
        // inmediato tras el login -- antes se pedía al final y, si la API de
        // perfil todavía no respondía y `fetchUserAttributes()` no exponía
        // el atributo custom, `accountId` quedaba en null hasta un F5.
        let idTokenAccountId = null;
        try {
          const session = await fetchAuthSession();
          if (!isMounted) return;
          const raw = session?.tokens?.accessToken;
          // normaliza a string de forma segura
          const accessToken =
            typeof raw === "string"
              ? raw
              : raw && typeof raw.toString === "function"
                ? raw.toString()
                : raw != null
                  ? String(raw)
                  : "";
          setAccessToken(accessToken);

          const idPayload = session?.tokens?.idToken?.payload || {};
          if (typeof idPayload["custom:account_id"] === "string" && idPayload["custom:account_id"]) {
            idTokenAccountId = idPayload["custom:account_id"];
          }

          // `cognito:groups` es un claim del ID token (no un atributo de
          // usuario), por eso se lee acá del payload ya decodificado por
          // Amplify en vez de fetchUserAttributes().
          const groupsClaim = idPayload["cognito:groups"];
          setCognitoGroups(Array.isArray(groupsClaim) ? groupsClaim : []);
        } catch (e) {
          console.warn("⚠️ Error fetching auth session:", e);
          if (isMounted) {
            setAccessToken("");
            setCognitoGroups([]);
          }
        }

        // Dispara en paralelo lo que no depende entre sí
        const [currentUser, attributes, profile] = await Promise.all([
          getCurrentUser(),
          fetchUserAttributes(),
          (async () => {
            try {
              const p = await UserProfileService.getProfile();
              return p && typeof p.account_id !== "undefined" ? p : null;
            } catch (e) {
              console.warn("⚠️ Error fetching profile data:", e);
              return null;
            }
          })()
        ]);

        if (!isMounted) return;

        setUserData({
          ...(typeof currentUser === "object" ? currentUser : { currentUser }),
          attributes
        });

        //Account Id Asignation
        const resolveAccountIdFromAttributes = (attrs) => {
          if (!attrs || typeof attrs !== "object") {
            return null;
          }
          if (typeof attrs["custom:account_id"] === "string" && attrs["custom:account_id"]) {
            return attrs["custom:account_id"];
          }
          if (typeof attrs.account_id === "string" && attrs.account_id) {
            return attrs.account_id;
          }
          if (typeof attrs.accountId === "string" && attrs.accountId) {
            return attrs.accountId;
          }
          return null;
        };

        const attributeAccountId = resolveAccountIdFromAttributes(attributes);
        const profileAccountId =
          profile && "account_id" in profile ? profile.account_id : null;
        const account_id =
          profileAccountId || attributeAccountId || idTokenAccountId || null;

        if (account_id) {
          setAccountId(account_id);
          try {
            if (typeof window !== "undefined" && window.localStorage) {
              window.localStorage.setItem("account_id", account_id);
            }
          } catch (storageError) {
            console.warn("⚠️ No pudimos sincronizar el account_id en localStorage:", storageError);
          }
        } else {
          // No pudimos resolver ninguna fuente. No pisamos con `null` un
          // valor que ya teníamos cacheado (seed inicial desde localStorage).
          setAccountId((prev) => prev || null);
        }

      } catch (err) {
        console.error("🚀 ~ Error fetching user data:", err);
        if (isMounted) {
          setAccountId((prev) => prev || null);
          setAccessToken("");
        }
      } finally {
        if (isMounted) {
          setAuthResolved(true);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Resuelve permisos granulares + rol del usuario una vez que ya sabemos
  // quién es (userData resuelto por el efecto de arriba). Tolerante a
  // fallos: el endpoint puede no estar desplegado aún o el backend puede
  // estar caído, y eso no debe romper el resto del contexto ni bloquear la
  // app -- simplemente deja permissions=[] / myRole=null (hasPermission ya
  // cae de vuelta a isPlatformRoot en ese caso).
  useEffect(() => {
    let isMounted = true;

    if (!userData) {
      return () => {
        isMounted = false;
      };
    }

    const fetchPermissions = async () => {
      try {
        const data = await getMyPermissions();
        if (!isMounted) return;
        setPermissions(Array.isArray(data?.permissions) ? data.permissions : []);
        setMyRole(
          data && (data.role_id || data.role_name)
            ? { role_id: data.role_id || null, role_name: data.role_name || null }
            : null
        );
      } catch (err) {
        console.warn("⚠️ Error fetching my permissions:", err);
        if (isMounted) {
          setPermissions([]);
          setMyRole(null);
        }
      }
    };

    fetchPermissions();
    return () => {
      isMounted = false;
    };
  }, [userData]);

  // Refresco de token bajo demanda
  const refreshAccessToken = async () => {
    try {
      const session = await fetchAuthSession();
      const raw = session?.tokens?.accessToken;
      const accessToken =
        typeof raw === "string"
          ? raw
          : raw && typeof raw.toString === "function"
            ? raw.toString()
            : raw != null
              ? String(raw)
              : "";
      setAccessToken(accessToken);
    } catch (err) {
      console.warn("🚀 ~ Error refreshing access token:", err);
    }
  };

  // Refresco FORZADO de sesión: fuerza a Amplify a intercambiar el refresh
  // token por un ID token nuevo, re-leyendo `cognito:groups` y
  // `custom:account_id`. Necesario porque el efecto de arriba (que resuelve
  // cognitoGroups) solo corre una vez al montar el provider, y
  // refreshAccessToken sólo toca accessToken -- si el backend acaba de
  // asignar un grupo de Cognito (alta de tenant / enrollment), la sesión
  // activa no se entera hasta forzar este refresh.
  const refreshSession = async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const raw = session?.tokens?.accessToken;
      const newAccessToken =
        typeof raw === "string"
          ? raw
          : raw && typeof raw.toString === "function"
            ? raw.toString()
            : raw != null
              ? String(raw)
              : "";
      setAccessToken(newAccessToken);

      const idPayload = session?.tokens?.idToken?.payload || {};
      const groupsClaim = idPayload["cognito:groups"];
      const newGroups = Array.isArray(groupsClaim) ? groupsClaim : [];
      setCognitoGroups(newGroups);

      const refreshedAccountId = idPayload["custom:account_id"];
      if (refreshedAccountId) {
        setAccountId(refreshedAccountId);
        try {
          if (typeof window !== "undefined" && window.localStorage) {
            window.localStorage.setItem("account_id", refreshedAccountId);
          }
        } catch {
          // ignore when storage fails
        }
      }

      return { accessToken: newAccessToken, cognitoGroups: newGroups };
    } catch (err) {
      console.warn("⚠️ Error refrescando la sesión (forceRefresh):", err);
      return null;
    }
  };

  // Update selected workspace and persist to localStorage
  const updateSelectedWorkspace = (workspace) => {
    setSelectedWorkspace(workspace);
    
    if (workspace) {
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.setItem("selected_workspace", JSON.stringify(workspace));
        }
      } catch (error) {
        console.warn("⚠️ Failed to persist workspace to localStorage:", error);
      }
    } else {
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.removeItem("selected_workspace");
        }
      } catch (error) {
        console.warn("⚠️ Failed to remove workspace from localStorage:", error);
      }
    }
  };

  // Fija (o borra, si configId es null) el agente elegido para un workspace y
  // lo persiste. Mismo patrón que updateSelectedWorkspace.
  const updateSelectedAgent = (workspaceId, configId) => {
    if (!workspaceId) return;
    setAgentByWorkspace((prev) => {
      const next = { ...prev };
      if (configId) {
        if (next[workspaceId] === configId) return prev;
        next[workspaceId] = configId;
      } else {
        if (!(workspaceId in next)) return prev;
        delete next[workspaceId];
      }
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.setItem("selected_agent_by_workspace", JSON.stringify(next));
        }
      } catch {
        // ignore
      }
      return next;
    });
  };

  const selectedAgentId =
    agentByWorkspace[selectedWorkspace?.semantic_workspace_id] ?? null;


  // Set up an interval to refresh the access token every 30 minutes
  useEffect(() => {
    const intervalId = setInterval(refreshAccessToken, 30 * 60 * 1000); // 30 minutes
    return () => clearInterval(intervalId); // Clear interval on unmount
  }, []);

  return {
    userData,
    setUserData,
    setAccessToken,
    accessToken,
    accountId,
    setAccountId,
    authResolved,
    selectedWorkspace,
    updateSelectedWorkspace,
    agentByWorkspace,
    updateSelectedAgent,
    selectedAgentId,
    cognitoGroups,
    isPlatformRoot: cognitoGroups.includes("root"),
    isTenantAdmin: cognitoGroups.includes("admin"),
    permissions,
    myRole,
    hasPermission: (key) => permissions.includes(key) || cognitoGroups.includes("root"),
    refreshSession
  };
};

// Context provider that provides user data and access token
export const DataProvider = ({ children }) => {
  const userDataContext = useUserData();
  return (
    <DataContext.Provider value={userDataContext}>
      {children}
    </DataContext.Provider>
  );
};
